import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";

import { PagedTable, SectionCard, StatusChip, formatDateTime, safeArray } from "../coreNetworkUiShared";
import {
  getSubmissionReviewUiState,
  submitApproveReview,
  submitRevisionReview,
  validateRevisionNote,
} from "./CoreNetworkSubmissionPanel.helpers";

function normalizeSubmissionFiles(filesJson) {
  if (Array.isArray(filesJson)) return filesJson;
  if (typeof filesJson === "string") {
    try {
      const parsed = JSON.parse(filesJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  return [];
}

function getSubmissionFileUrl(file) {
  return String(
    file?.url
      || file?.downloadUrl
      || file?.fileUrl
      || file?.previewUrl
      || file?.path
      || file?.sourceFilePath
      || ""
  ).trim();
}

function getSubmissionFileLabel(file, index) {
  const explicitLabel = String(
    file?.fileName
      || file?.name
      || file?.label
      || file?.originalName
      || ""
  ).trim();
  if (explicitLabel) return explicitLabel;

  const url = getSubmissionFileUrl(file);
  if (!url) return `File ${index + 1}`;

  const sanitizedUrl = url.split("?")[0].split("#")[0];
  const lastSegment = sanitizedUrl.split("/").filter(Boolean).pop();
  return lastSegment || `File ${index + 1}`;
}

function isImageFile(file) {
  const type = String(file?.mimeType || file?.type || "").toLowerCase();
  const url = getSubmissionFileUrl(file).toLowerCase();
  return type.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(url);
}

function renderSubmissionFiles(filesJson) {
  const files = normalizeSubmissionFiles(filesJson);
  if (!files.length) return "-";

  return (
    <Stack spacing={1} sx={{ minWidth: 220 }}>
      {files.map((file, index) => {
        const url = getSubmissionFileUrl(file);
        const label = getSubmissionFileLabel(file, index);
        const imageFile = isImageFile(file);

        return (
          <Stack
            key={`${label}-${url || index}`}
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
              p: 1,
              border: "1px solid #edf2f7",
              borderRadius: 1.5,
              backgroundColor: "#fbfcfd",
            }}
          >
            {imageFile && url ? (
              <Box
                component="img"
                src={url}
                alt={label}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1,
                  objectFit: "cover",
                  border: "1px solid #d9e2ec",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1,
                  border: "1px solid #d9e2ec",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {String(label.split(".").pop() || "FILE").slice(0, 4).toUpperCase()}
                </Typography>
              </Box>
            )}
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              {url ? (
                <MuiLink
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontWeight: 600, wordBreak: "break-word" }}
                >
                  {label}
                </MuiLink>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {label}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {imageFile ? "Image preview available" : url ? "Open file in a new tab" : "File reference only"}
              </Typography>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}

export default function CoreNetworkSubmissionPanel({
  submissions = [],
  onCreateSubmission,
  onReviewSubmission,
  creating = false,
  reviewingId = null,
}) {
  const [submissionText, setSubmissionText] = useState("");
  const [submissionNotes, setSubmissionNotes] = useState("");
  const [revisionDialogSubmission, setRevisionDialogSubmission] = useState(null);
  const [revisionNote, setRevisionNote] = useState("");
  const [revisionError, setRevisionError] = useState("");

  const handleCreate = async () => {
    const filesJson = submissionText
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((url) => ({ url }));
    await onCreateSubmission?.({
      filesJson,
      notes: submissionNotes || null,
    });
    setSubmissionText("");
    setSubmissionNotes("");
  };

  const closeRevisionDialog = () => {
    setRevisionDialogSubmission(null);
    setRevisionNote("");
    setRevisionError("");
  };

  const handleApprove = async (row) => {
    await submitApproveReview({ submission: row, onReviewSubmission });
  };

  const handleOpenRevisionDialog = (row) => {
    setRevisionDialogSubmission(row);
    setRevisionNote("");
    setRevisionError("");
  };

  const handleConfirmRevision = async () => {
    const trimmedNote = validateRevisionNote(revisionNote);
    if (!trimmedNote) {
      setRevisionError("Revision note is required.");
      return;
    }

    const result = await submitRevisionReview({
      submission: revisionDialogSubmission,
      note: trimmedNote,
      onReviewSubmission,
    });

    if (result?.ok) {
      closeRevisionDialog();
    } else if (result?.error) {
      setRevisionError(result.error);
    }
  };

  return (
    <SectionCard title="Submissions" subtitle="Deliverables and review outcomes for this work request.">
      <PagedTable
        rows={safeArray(submissions)}
        columns={[
          { key: "status", label: "Status", render: (row) => <StatusChip value={row.status} /> },
          {
            key: "version",
            label: "Version",
            render: (row) => `v${Number(row.version || 1)}`,
          },
          { key: "submittedAt", label: "Submitted", render: (row) => formatDateTime(row.submittedAt) },
          {
            key: "filesJson",
            label: "Files",
            minWidth: 280,
            render: (row) => renderSubmissionFiles(row.filesJson),
          },
          { key: "notes", label: "Notes", render: (row) => row.notes || "-" },
          { key: "reviewNotes", label: "Review Notes", render: (row) => row.reviewNotes || "-" },
          {
            key: "review",
            label: "Review Action",
            minWidth: 220,
            render: (row) => {
              const reviewUiState = getSubmissionReviewUiState(row, {
                canReview: Boolean(onReviewSubmission),
              });
              const rowSaving = reviewingId === row.id;

              if (reviewUiState.kind === "actions") {
                return (
                  <Stack spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {reviewUiState.label}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="success"
                      size="small"
                      startIcon={<TaskAltOutlinedIcon />}
                      onClick={() => handleApprove(row)}
                      disabled={rowSaving}
                      title="Accept this submission."
                    >
                      {rowSaving ? "Saving..." : "Approve"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RateReviewOutlinedIcon />}
                      onClick={() => handleOpenRevisionDialog(row)}
                      disabled={rowSaving}
                      title="Send the provider requested changes."
                    >
                      Request revision
                    </Button>
                  </Stack>
                );
              }

              if (reviewUiState.kind === "approve_only") {
                return (
                  <Stack spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {reviewUiState.label}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="success"
                      size="small"
                      startIcon={<TaskAltOutlinedIcon />}
                      onClick={() => handleApprove(row)}
                      disabled={rowSaving}
                      title="Accept this submission."
                    >
                      {rowSaving ? "Saving..." : "Approve"}
                    </Button>
                  </Stack>
                );
              }

              return (
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {reviewUiState.label}
                  </Typography>
                  {rowSaving ? (
                    <Typography variant="caption" color="text.secondary">
                      Saving...
                    </Typography>
                  ) : null}
                </Stack>
              );
            },
          },
        ]}
        emptyTitle="No submissions yet"
        emptyDescription="Submissions will appear here once a provider or internal owner delivers work."
      />

      <Dialog open={Boolean(revisionDialogSubmission)} onClose={closeRevisionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Request revision</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Tell the provider what needs to change.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Revision note"
              placeholder="Describe the changes needed..."
              multiline
              minRows={4}
              value={revisionNote}
              onChange={(event) => {
                setRevisionNote(event.target.value);
                if (revisionError) setRevisionError("");
              }}
              error={Boolean(revisionError)}
              helperText={revisionError || " "}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevisionDialog} disabled={reviewingId === revisionDialogSubmission?.id}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmRevision}
            disabled={reviewingId === revisionDialogSubmission?.id}
          >
            {reviewingId === revisionDialogSubmission?.id ? "Saving..." : "Request revision"}
          </Button>
        </DialogActions>
      </Dialog>

      {onCreateSubmission ? (
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Add submission
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="File URLs" helperText="Comma-separated URLs or file references" value={submissionText} onChange={(event) => setSubmissionText(event.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Notes" value={submissionNotes} onChange={(event) => setSubmissionNotes(event.target.value)} />
            </Grid>
          </Grid>
          <div>
            <Button variant="outlined" onClick={handleCreate} disabled={creating || !submissionText.trim()}>
              {creating ? "Saving..." : "Create submission"}
            </Button>
          </div>
        </Stack>
      ) : null}
    </SectionCard>
  );
}
